/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, { DEFAULT_FONT_SIZE } from './WireframeBase';
import tools from '../tools';
import wireframePagination from '../assets/icons/wireframePagination.svg';
import wireframeStylePaginationUnframed from '../assets/icons/wireframeStylePaginationUnframed.svg';
import wireframeStylePaginationFramed from '../assets/icons/wireframeStylePaginationFramed.svg';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { SPACER_1, SPACER_2, SPACER_3 } from '../utils/style';
import { Text } from '@lib/canvasx/components/Text';
import {
  type TextWithIconProps,
  TextWithIcons,
} from '../components/wireframes/TextWithIcons';
import { range } from 'lodash';
import {
  WIREFRAME_PRIMARY_COLOR,
  WIREFRAME_SELECTION_COLOR,
} from '../utils/wireframes';
import { type IconData } from '../types';
import { SvgIcon } from '../components/wireframes/SvgIcon';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type WireframeItem from './WireframeItem';

const PADDING_VERTICAL = SPACER_2;
const PADDING_HORIZONTAL = SPACER_3;
const BORDER_RADIUS = SPACER_1;
const BORDER_WIDTH = 2;
const INITIAL_PAGINATION_COUNT = 6;

class WireframePagination extends WireframeBase {
  initItems() {
    if (this.fabricObject.items.items.length === 0) {
      range(INITIAL_PAGINATION_COUNT).forEach(i => {
        const state = i === 1 ? 'selected' : 'default';
        this.fabricObject.items.add({ value: i + 1, state });
      });
    }
  }

  tools() {
    if (this.activeKey && !this.activeKey.includes('button')) {
      return [[tools.ToolWireframeItemState]];
    }
    return [
      [tools.ToolWireframeAddItemPlus, tools.ToolWireframeAddItemMinus],
      [tools.ToolWireframeComponentStyle],
      [tools.ToolPaginationStyleFlags],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
    ];
  }

  static defaultWidth(): number {
    return 333;
  }

  static defaultHeight(): number {
    return 33;
  }

  static iconSvg() {
    return wireframePagination;
  }

  static externalName(): string {
    return 'Pagination';
  }

  static componentStyles() {
    return {
      unframed: wireframeStylePaginationUnframed,
      framed: wireframeStylePaginationFramed,
    };
  }

  componentStyles() {
    return WireframePagination.componentStyles();
  }

  defaultText() {
    return (this.fabricObject.items?.items.length + 1).toString() ?? '1';
  }

  setItemState(state: string) {
    super.setItemState(state);

    const activeItem = this.activeItem();
    // Pagination should always have exactly one active item selected. If the
    // user sets the current item to default, we will set either the first or
    // second item to selected
    if (
      activeItem &&
      !this.fabricObject.items.items.some(item => item.state === 'selected')
    ) {
      if (
        this.fabricObject.items.items.indexOf(activeItem as WireframeItem) === 0
      ) {
        // If the first item was set to default, then set the second item to selected
        this.fabricObject.items.items[1].state = 'selected';
      } else {
        // Otherwise, set the first item to selected by default
        this.fabricObject.items.items[0].state = 'selected';
      }
    }
  }

  toolRemoveItemEnabled() {
    return this.fabricObject.items.items.length > 2;
  }

  styleFlagEnabled(key) {
    // Avoid an empty button by only enabling the button text and icon flags if
    // one of the two is already enabled
    if (['showButtonText', 'showIcons'].includes(key)) {
      const otherKey =
        key === 'showButtonText' ? 'showIcons' : 'showButtonText';
      return this.fabricObject[otherKey];
    }

    if (key === 'showEllipses' && this.fabricObject.items.items.length < 3) {
      return false;
    }
    return true;
  }

  buttonStyle(location: 'left' | 'right'): TextWithIconProps {
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const framed = this.framed;
    const buttonRadius = this.buttonRadius(location);
    return {
      border: framed ? {} : { color: fill, lineWidth: BORDER_WIDTH },
      borderRadius: framed ? buttonRadius : BORDER_RADIUS,
      fill: { color: fill, opacity: 0.05 },
      color: fill,
      padding: { vertical: PADDING_VERTICAL, horizontal: PADDING_HORIZONTAL },
      gap: 10,
      size: 14,
    };
  }

  buttonIcon(location: 'left' | 'right') {
    if (!this.fabricObject.showIcons) return null;
    const prefix = 'fas';
    const icon = (
      location === 'left'
        ? { prefix, iconName: 'angle-left' }
        : { prefix, iconName: 'angle-right' }
    ) as IconData;
    return location === 'left' ? { iconLeft: icon } : { iconRight: icon };
  }

  buttonRadius(location: 'left' | 'right') {
    const radius = this.fabricObject.height / 2;
    return location === 'left'
      ? { topLeft: radius, bottomLeft: radius }
      : { topRight: radius, bottomRight: radius };
  }

  buttonElement(location: 'left' | 'right') {
    const { showButtonText, textEditing } = this.fabricObject;
    const textKey = location === 'left' ? 'buttonLeft' : 'buttonRight';
    const value = this.fabricObject[textKey];
    return (
      <TextWithIcons
        textKey={textKey}
        textEditing={textEditing && this.activeKey.includes(textKey)}
        size={DEFAULT_FONT_SIZE}
        onChange={value => {
          this.onButtonTextChange(location, value);
        }}
        {...this.buttonStyle(location)}
        {...this.buttonIcon(location)}
        verticalAlignment='center'
      >
        {showButtonText ? value : null}
      </TextWithIcons>
    );
  }

  paginationElement(index: number) {
    const { items, showEllipses } = this.fabricObject;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const item = items.items[index];
    let value = item.value;
    const selected = item.state === 'selected';

    if (showEllipses) {
      // Replace the second to last item with ellipsis
      if (index === items.items.length - 2) {
        return (
          <SvgIcon
            iconData='ellipsis'
            iconFill={selected ? 'white' : fill}
            size={DEFAULT_FONT_SIZE}
          />
        );
      }

      // Replace the last item with double the number of items
      if (index === items.items.length - 1) {
        value = (items.items.length * 2).toString();
      }
    }

    return (
      <Text size={DEFAULT_FONT_SIZE} color={selected ? 'white' : fill}>
        {value}
      </Text>
    );
  }

  onButtonTextChange(location: 'left' | 'right', value: string) {
    const key = location === 'left' ? 'buttonLeft' : 'buttonRight';
    if (value.length > 0) {
      this.fabricObject.set(key, value);
      this.updateDimensions = true;
      this.checkSetDimension = true;
      this.fabricObject.canvas.requestRenderAll();
      this.fabricObject.controller.undoManager.completeUndoGroup();
    }
  }

  get framed() {
    return this.fabricObject.style === 'framed';
  }

  get selectedPageStyle() {
    return {
      fill: this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR,
      color: 'white',
    };
  }

  wasSet(keyOrPath: KeyPath): void {
    if (keyOrPath.includes('itemsData')) {
      // Ellipses dont make sense with less than 3 items
      if (this.fabricObject.items.items.length < 3) {
        this.fabricObject.set('showEllipses', false);
      }
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const { height, scaleX, items, showEllipses } = this.fabricObject;
    const framed = this.framed;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;

    let renderedDimensions;

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexRow
            verticalAlignment='center'
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
            border={
              framed ? { color: fill, lineWidth: BORDER_WIDTH } : 'transparent'
            }
            borderRadius={framed ? height : 0}
            gap={framed ? 0 : SPACER_2}
          >
            {this.buttonElement('left')}
            <FlexRow gap={1}>
              {range(items.items.length).map(i => {
                const item = items.items[i];
                const { id, state } = item;
                const selected = state === 'selected';
                const active = this.activeKey.includes(id);
                const props = selected ? this.selectedPageStyle : {};
                const pagination = this.paginationElement(i);
                let key: string | number = id;
                if (showEllipses && i === items.items.length - 2) {
                  // This will make the ellipsis unselectable as an individual item
                  key = '';
                }

                return (
                  <FlexRow
                    key={key}
                    verticalAlignment='center'
                    padding={{
                      vertical: PADDING_VERTICAL,
                      horizontal: PADDING_HORIZONTAL,
                    }}
                    border={active ? WIREFRAME_SELECTION_COLOR : 'transparent'}
                    borderRadius={framed ? 0 : BORDER_RADIUS}
                    {...props}
                  >
                    {pagination}
                  </FlexRow>
                );
              })}
            </FlexRow>
            {this.buttonElement('right')}
          </FlexRow>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframePagination;
